<script lang="ts" setup>
    import { computed, onMounted, ref, watch } from 'vue'
    import { useDebounceFn } from '@vueuse/core'
    import {
        itemsPerPageOptions,
        itemsPerPageText,
        loadingText,
        noItemsText,
        type SortItem,
        type TableHeaders
    } from '@/js/utils'
    import { type KennzahlStudiengang } from '@/js/model'
    import { useKennwerteAccess } from '@/js/components/Common/useKennwerteAccess'
    import { useFachbereichStore, useKennzahlStudiengangStore, useStudiengangStore } from '@/js/stores'

    const SEARCH_DEBOUNCE_MS = 250

    const kennzahlStudiengangStore = useKennzahlStudiengangStore()
    const fachbereichStore = useFachbereichStore()
    const studiengangStore = useStudiengangStore()
    const {seesAllKennwerte, allowedFachbereichIds, allowedStudiengangIds} = useKennwerteAccess()

    const headers: TableHeaders = [
        { key: 'abkuerzung', title: 'Abkürzung', sortable: true },
        { key: 'name', title: 'Bezeichnung', sortable: true },
        { key: 'isDual', title: 'Dual', sortable: true, align: 'center' }
    ]

    const items = ref<Array<KennzahlStudiengang>>([])
    const totalItems = ref(0)
    const page = ref(1)
    const itemsPerPage = ref(25)
    const sortBy = ref<SortItem[]>([{ key: 'label', order: 'asc' }])
    const search = ref('')
    /** null = "Alle Fachbereiche", sonst die Fachbereich-ID. */
    const selectedFachbereichId = ref<string | null>(null)

    /**
     * Eingeschränkter Scope: nur die für den User erlaubten Fachbereiche anzeigen
     * (direkte FB-IDs vom Studiendekan + die FBs der zugewiesenen Studiengänge bei SFB/QMB).
     */
    const allowedFachbereichIdSet = computed<Set<string> | null>(() => {
        if (seesAllKennwerte.value) {
            return null
        }
        const ids = new Set<string>(allowedFachbereichIds.value)
        for (const sgId of allowedStudiengangIds.value) {
            const sg = studiengangStore.studiengaenge.find((s) => s.studiengangId === sgId)
            if (!sg || !sg.fachbereich) {
                continue
            }
            const fb = fachbereichStore.getFachbereiche.find((f) => f['@id'] === sg.fachbereich)
            if (fb) {
                ids.add(fb.fachbereichId)
            }
        }
        return ids
    })

    const fachbereichSelectItems = computed(() => [
        { title: 'Alle Fachbereiche', value: null },
        ...fachbereichStore.getFachbereiche
            .filter((f) =>
                allowedFachbereichIdSet.value === null || allowedFachbereichIdSet.value.has(f.fachbereichId)
            )
            .map((f) => ({
                title: `FB${f.hsaId} - ${f.kurzName}`,
                value: f.fachbereichId
            }))
    ])

    /**
     * AbortController des aktuell laufenden Requests. Bei jeder neuen Anfrage
     * (Suche, Pagination, Sort) wird der vorherige Request abgebrochen, damit
     * Race-Conditions in der UI nicht zu Stale-Daten führen.
     */
    let activeController: AbortController | null = null

    async function loadItems(): Promise<void> {
        activeController?.abort()
        const controller = new AbortController()
        activeController = controller

        const order: Record<string, 'asc' | 'desc'> = {}
        for (const item of sortBy.value) {
            if (item.order === 'asc' || item.order === 'desc') {
                order[item.key] = item.order
            }
        }

        const result = await kennzahlStudiengangStore.fetchKennzahlStudiengaengePaginated({
            page: page.value,
            itemsPerPage: itemsPerPage.value,
            label: search.value.trim() || undefined,
            fachbereichId: selectedFachbereichId.value ?? undefined,
            order: Object.keys(order).length > 0 ? order : undefined,
            signal: controller.signal
        })
        // Abgebrochene Anfrage → vorigen State behalten; ein neuer Request läuft bereits.
        if (result === null) {
            return
        }
        items.value = result.items
        totalItems.value = result.totalItems
        if (activeController === controller) {
            activeController = null
        }
    }

    const debouncedSearch = useDebounceFn(() => {
        page.value = 1
        void loadItems()
    }, SEARCH_DEBOUNCE_MS)

    watch(search, () => {
        void debouncedSearch()
    })

    const debouncedFachbereichChange = useDebounceFn(() => {
        page.value = 1
        void loadItems()
    }, SEARCH_DEBOUNCE_MS)

    watch(selectedFachbereichId, () => {
        void debouncedFachbereichChange()
    })

    function handleOptionsUpdate(opts: { page: number; itemsPerPage: number; sortBy: SortItem[] }) {
        page.value = opts.page
        itemsPerPage.value = opts.itemsPerPage
        sortBy.value = opts.sortBy
        void loadItems()
    }

    onMounted(() => {
        // Fachbereiche für den Filter nur laden, wenn der Store noch leer ist —
        // andere Pages (z.B. Navigation) haben sie evtl. schon geholt.
        if (fachbereichStore.fachbereiche.length === 0) {
            void fachbereichStore.fetchFachbereiche()
        }
        // Studiengänge brauchen wir nur, um SFB/QMB-Studiengangscope auf FB-IDs zu mappen.
        if (studiengangStore.studiengaenge.length === 0 && !seesAllKennwerte.value) {
            void studiengangStore.fetchStudiengaenge()
        }
        void loadItems()
    })
</script>

<template>
    <div>
        <v-data-table-server
            v-model:items-per-page="itemsPerPage"
            v-model:page="page"
            v-model:sort-by="sortBy"
            :headers="headers"
            :items="items"
            :items-length="totalItems"
            :loading="kennzahlStudiengangStore.isLoading"
            :items-per-page-options="itemsPerPageOptions"
            :items-per-page-text="itemsPerPageText"
            :no-data-text="noItemsText"
            :loading-text="loadingText"
            density="compact"
            hide-default-footer
            mobile-breakpoint="md"
            data-test="kennzahl-studiengang-table"
            multi-sort
            @update:options="handleOptionsUpdate"
        >
            <template #top>
                <div class="d-flex flex-wrap ga-4 justify-space-between align-center border-b-sm mb-4 pa-2">
                    <div class="d-flex flex-wrap ga-4 align-center">
                        <v-text-field
                            v-model="search"
                            label="Suche"
                            placeholder="Abkürzung oder Bezeichnung"
                            prepend-inner-icon="mdi-magnify"
                            density="compact"
                            variant="outlined"
                            hide-details
                            clearable
                            autocomplete="off"
                            name="kennzahl-studiengang-search"
                            style="min-width: 280px; max-width: 320px"
                        />
                        <v-select
                            v-model="selectedFachbereichId"
                            :items="fachbereichSelectItems"
                            label="Fachbereich"
                            density="compact"
                            variant="outlined"
                            hide-details
                            style="min-width: 240px; max-width: 320px"
                        />
                    </div>
                    <v-data-table-footer
                        :items-per-page-text="itemsPerPageText"
                        :items-per-page-options="itemsPerPageOptions"
                        show-current-page
                    />
                </div>
            </template>

            <template #item.isDual="{ item }">
                <v-icon
                    v-if="item.isDual"
                    icon="mdi-check"
                    color="primary"
                    title="Dualer Studiengang"
                />
            </template>
        </v-data-table-server>
    </div>
</template>

<style scoped lang="scss"></style>
